// adminportal/src/pages/AdminSetup.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Shield, CheckCircle, Smartphone } from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import Copyright from '../components/Copyright';
import { CognitoAuthService } from '../services/cognitoAuth';
import { markAuthed } from '../auth';
import { apiUrl } from '../config';

// DEBUG: Force console log to verify component is loading
console.log('🔍 AdminSetup component loaded!');

interface AdminSetupForm {
  username: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  confirmationCode: string;
  mfaCode: string;
}

type SetupStep = 'login' | 'password-reset' | 'mfa-setup' | 'complete';

export default function AdminSetup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState<SetupStep>('login');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // const [userDetails, setUserDetails] = useState<{ username: string } | null>(null);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSecret, setMfaSecret] = useState<string>('');

  const [form, setForm] = useState<AdminSetupForm>({
    username:
      searchParams.get('username') || localStorage.getItem('adminPasswordChangeUsername') || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    confirmationCode: '',
    mfaCode: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Check if we need to start with password reset step
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam === 'password-reset') {
      setStep('password-reset');
    }
  }, [searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!form.username || !form.currentPassword) {
      setMessage('❌ Username and password are required');
      setLoading(false);
      return;
    }

    try {
      const result = await CognitoAuthService.login(form.username, form.currentPassword);

      if (result.success && result.user) {
        // setUserDetails(result.user);
        setMessage('✅ Login successful! Please complete your admin account setup.');
        setStep('password-reset');
      } else if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // setUserDetails({ username: form.username });
        setMessage('✅ Please set your new password to continue.');
        setStep('password-reset');
      } else {
        setMessage(result.error || 'Login failed');
      }
    } catch (err: unknown) {
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!form.newPassword || !form.confirmPassword) {
      setMessage('❌ New password and confirmation are required');
      setLoading(false);
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setMessage('❌ Passwords do not match');
      setLoading(false);
      return;
    }

    if (form.newPassword.length < 8) {
      setMessage('❌ Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      // Get session from localStorage
      const session = localStorage.getItem('adminPasswordChangeSession');
      if (!session) {
        setMessage('❌ No session found. Please try logging in again.');
        setLoading(false);
        return;
      }

      // Use CognitoAuthService to respond to the password challenge
      const result = await CognitoAuthService.respondToNewPasswordChallenge(
        form.username,
        form.newPassword,
        session
      );

      if (result.success) {
        // Debug: Check what tokens are available after password reset
        console.log('🔍 AdminSetup Password Reset Response Debug:');
        console.log('  - Result:', result);
        console.log('  - User:', result.user);
        console.log('  - User Type:', result.userType);

        // Debug: Check what's in localStorage after password reset (tokens should be stored by storeTokens method)
        console.log('🔍 localStorage after password reset:');
        console.log(
          '  - cognito_access_token:',
          localStorage.getItem('cognito_access_token') ? 'Present' : 'Missing'
        );
        console.log(
          '  - cognito_refresh_token:',
          localStorage.getItem('cognito_refresh_token') ? 'Present' : 'Missing'
        );
        console.log(
          '  - cognito_id_token:',
          localStorage.getItem('cognito_id_token') ? 'Present' : 'Missing'
        );
        console.log('  - All localStorage keys:', Object.keys(localStorage));

        // Clear stored session data
        localStorage.removeItem('adminPasswordChangeSession');
        localStorage.removeItem('adminPasswordChangeUsername');

        setMessage('✅ Password updated successfully! Now you can optionally set up MFA.');
        // setUserDetails(result.user);
        setStep('mfa-setup');
      } else {
        setMessage(`❌ ${result.error || 'Password change failed'}`);
      }
    } catch (err: unknown) {
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetup = async (e?: React.FormEvent) => {
    console.log('🔍 handleMfaSetup function called');
    console.log('🔍 Event:', e);
    if (e) {
      e.preventDefault();
    }
    setLoading(true);
    setMessage('');

    // Debug: Check what tokens are available
    const accessToken = localStorage.getItem('cognito_access_token');
    const refreshToken = localStorage.getItem('cognito_refresh_token');
    const idToken = localStorage.getItem('cognito_id_token');

    console.log('🔍 AdminSetup MFA Setup Debug:');
    console.log('  - Access Token:', accessToken ? 'Present' : 'Missing');
    console.log('  - Refresh Token:', refreshToken ? 'Present' : 'Missing');
    console.log('  - ID Token:', idToken ? 'Present' : 'Missing');
    console.log('  - MFA Enabled:', mfaEnabled);
    console.log('  - MFA Code:', form.mfaCode);
    console.log('  - All localStorage keys:', Object.keys(localStorage));

    if (mfaEnabled && !form.mfaCode) {
      setMessage('❌ MFA code is required when MFA is enabled');
      setLoading(false);
      return;
    }

    try {
      if (mfaEnabled) {
        // Check if user is authenticated
        if (!accessToken) {
          setMessage('❌ You must be logged in to set up MFA. Please log in first.');
          setLoading(false);
          return;
        }

        // Step 1: Associate the software token
        const response = await fetch(apiUrl('mfa/associate-software-token'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accessToken,
          }),
        });

        const data = await response.json();
        console.log('🔍 MFA Associate Response:', data);
        console.log('🔍 Response status:', response.status);
        console.log('🔍 Response ok:', response.ok);
        console.log('🔍 Data keys:', Object.keys(data));
        console.log('🔍 Secret code in response:', data.secretCode);
        console.log('🔍 Secret code type:', typeof data.secretCode);

        if (!response.ok) {
          console.log('❌ MFA Associate failed:', data);
          throw new Error(data.error || 'Failed to associate MFA token');
        }

        // Check if secretCode exists in response (API returns secretCode, not secretKey)
        if (data.secretCode) {
          console.log('✅ Secret code found, storing it');
          alert(`Secret code found: ${data.secretCode.substring(0, 10)}...`); // Temporary alert to test
          setMfaSecret(data.secretCode);
          setMessage(
            '✅ MFA setup started. Please scan the QR code or enter the secret key in your authenticator app.'
          );
        } else {
          console.log('❌ No secret code in response');
          console.log('🔍 Full response data:', JSON.stringify(data, null, 2));
          alert(`No secret code in response: ${JSON.stringify(data)}`); // Temporary alert to test
          setMessage('❌ MFA setup failed: No secret code received from server');
        }

        // Don't proceed to verification yet - wait for user to scan QR code and enter code
        // Stay on MFA setup step to show QR code
      } else {
        setMessage('✅ MFA setup skipped. You can enable it later in your profile.');
        setStep('complete');
      }
    } catch (err: unknown) {
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle MFA verification when user enters the code
  const handleMfaVerification = async () => {
    if (!form.mfaCode || form.mfaCode.length !== 6) {
      setMessage('❌ Please enter a valid 6-digit MFA code');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const accessToken = localStorage.getItem('cognito_access_token');
      if (!accessToken) {
        setMessage('❌ You must be logged in to verify MFA. Please log in first.');
        setLoading(false);
        return;
      }

      // Step 2: Verify the MFA code
      const verifyResponse = await fetch(apiUrl('mfa/verify-software-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
          userCode: form.mfaCode,
        }),
      });

      const verifyData = await verifyResponse.json();
      console.log('🔍 MFA Verify Response:', verifyData);

      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Failed to verify MFA code');
      }

      // Step 3: Set MFA preference
      const preferenceResponse = await fetch(apiUrl('mfa/set-mfa-preference'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
        }),
      });

      const preferenceData = await preferenceResponse.json();
      console.log('🔍 MFA Preference Response:', preferenceData);

      if (!preferenceResponse.ok) {
        throw new Error(preferenceData.error || 'Failed to set MFA preference');
      }

      setMessage('✅ MFA setup completed successfully!');
      setStep('complete');
    } catch (err: unknown) {
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, color: 'text-slate-400', label: '' };
    if (password.length < 8) return { strength: 1, color: 'text-red-400', label: 'Weak' };
    if (password.length < 12) return { strength: 2, color: 'text-yellow-400', label: 'Fair' };
    if (password.length < 16) return { strength: 3, color: 'text-blue-400', label: 'Good' };
    return { strength: 4, color: 'text-green-400', label: 'Strong' };
  };

  const passwordStrength = getPasswordStrength(form.newPassword);

  const renderStepContent = () => {
    switch (step) {
      case 'login':
        return (
          <form onSubmit={handleLogin} className='space-y-4'>
            <div className='text-center mb-6'>
              <h2 className='text-xl font-semibold text-slate-200 mb-2'>Admin Account Setup</h2>
              <p className='text-slate-400'>
                Please log in to complete your admin account configuration
              </p>
            </div>

            <div>
              <label className='block text-sm font-medium text-slate-300 mb-2'>Username *</label>
              <div className='relative'>
                <input
                  type='text'
                  name='username'
                  value={form.username}
                  onChange={handleChange}
                  className='w-full px-3 py-2 pl-12 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
                  placeholder='Enter your username'
                  required
                />
                <Lock className='absolute left-3 top-2.5 h-5 w-5 text-slate-400' />
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-slate-300 mb-2'>
                Current Password *
              </label>
              <div className='relative'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name='currentPassword'
                  value={form.currentPassword}
                  onChange={handleChange}
                  className='w-full px-3 py-2 pr-10 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
                  placeholder='Enter your current password'
                  required
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='absolute right-3 top-2.5 text-slate-400 hover:text-slate-300'
                >
                  {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                </button>
              </div>
            </div>

            <Button
              type='submit'
              disabled={loading || !form.username || !form.currentPassword}
              className='w-full'
            >
              {loading ? 'Logging In...' : 'Continue Setup'}
            </Button>
          </form>
        );

      case 'password-reset':
        return (
          <form onSubmit={handlePasswordReset} className='space-y-4'>
            <div className='text-center mb-6'>
              <h2 className='text-xl font-semibold text-slate-200 mb-2'>Set Your Admin Password</h2>
              <p className='text-slate-400'>Choose a strong password for your admin account</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-slate-300 mb-2'>
                New Password *
              </label>
              <div className='relative'>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  name='newPassword'
                  value={form.newPassword}
                  onChange={handleChange}
                  className='w-full px-3 py-2 pr-10 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
                  placeholder='Enter new password'
                  required
                />
                <button
                  type='button'
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className='absolute right-3 top-2.5 text-slate-400 hover:text-slate-300'
                >
                  {showNewPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                </button>
              </div>

              {/* Password strength indicator */}
              {form.newPassword && (
                <div className='mt-2'>
                  <div className='flex items-center gap-2 mb-1'>
                    <div className='flex gap-1'>
                      {[1, 2, 3, 4].map(level => (
                        <div
                          key={level}
                          className={`h-1 w-8 rounded ${
                            level <= passwordStrength.strength
                              ? passwordStrength.color.replace('text-', 'bg-')
                              : 'bg-slate-600'
                          }`}
                        />
                      ))}
                    </div>
                    <span className={`text-xs ${passwordStrength.color}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className='text-xs text-slate-500'>
                    Password must be at least 8 characters long
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium text-slate-300 mb-2'>
                Confirm New Password *
              </label>
              <div className='relative'>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name='confirmPassword'
                  value={form.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 pr-10 bg-slate-700 border rounded text-slate-100 focus:outline-none ${
                    form.confirmPassword && form.newPassword !== form.confirmPassword
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-white/10 focus:border-blue-500'
                  }`}
                  placeholder='Confirm new password'
                  required
                />
                <button
                  type='button'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className='absolute right-3 top-2.5 text-slate-400 hover:text-slate-300'
                >
                  {showConfirmPassword ? (
                    <EyeOff className='h-5 w-5' />
                  ) : (
                    <Eye className='h-5 w-5' />
                  )}
                </button>
              </div>

              {form.confirmPassword && form.newPassword !== form.confirmPassword && (
                <p className='text-xs text-red-400 mt-1'>Passwords do not match</p>
              )}
            </div>

            <Button
              type='submit'
              disabled={
                loading ||
                !form.newPassword ||
                !form.confirmPassword ||
                form.newPassword !== form.confirmPassword
              }
              className='w-full'
            >
              {loading ? 'Updating Password...' : 'Continue to MFA Setup'}
            </Button>

            <div className='text-center'>
              <button
                type='button'
                onClick={() => setStep('login')}
                className='text-sm text-slate-400 hover:text-slate-300 transition-colors'
              >
                ← Back to Login
              </button>
            </div>
          </form>
        );

      case 'mfa-setup':
        return (
          <div className='space-y-4'>
            <div className='text-center mb-6'>
              <h2 className='text-xl font-semibold text-slate-200 mb-2'>
                Multi-Factor Authentication (Optional)
              </h2>
              <p className='text-slate-400'>Add an extra layer of security to your admin account</p>
            </div>

            <div className='flex items-center gap-3 mb-4'>
              <input
                type='checkbox'
                id='enableMfa'
                checked={mfaEnabled}
                onChange={e => setMfaEnabled(e.target.checked)}
                className='w-4 h-4 text-blue-600 bg-slate-700 border-white/10 rounded focus:ring-blue-500 focus:ring-2'
              />
              <label htmlFor='enableMfa' className='text-sm text-slate-300'>
                Enable Multi-Factor Authentication (Recommended for admin accounts)
              </label>
            </div>

            {mfaEnabled && (
              <div className='space-y-4'>
                {/* Show QR code and secret key if available */}
                {mfaSecret ? (
                  <div className='bg-blue-500/20 border border-blue-500/30 rounded-lg p-4'>
                    <h3 className='text-sm font-medium text-blue-200 mb-3'>
                      Scan QR Code or Enter Secret Key
                    </h3>
                    <div className='space-y-3'>
                      {/* QR Code would go here - for now show the secret key */}
                      <div className='bg-slate-800 p-3 rounded border'>
                        <p className='text-xs text-slate-400 mb-1'>Secret Key:</p>
                        <p className='text-sm font-mono text-slate-200 break-all'>{mfaSecret}</p>
                      </div>
                      <p className='text-xs text-blue-300'>
                        Enter this secret key manually in your authenticator app, or scan the QR
                        code if available.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className='bg-blue-500/20 border border-blue-500/30 rounded-lg p-4'>
                    <div className='flex items-start gap-3'>
                      <Shield className='h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0' />
                      <div>
                        <h3 className='text-sm font-medium text-blue-200 mb-1'>
                          MFA Setup Instructions
                        </h3>
                        <ol className='text-xs text-blue-300 space-y-1'>
                          <li>
                            1. Download an authenticator app (Google Authenticator, Authy, etc.)
                          </li>
                          <li>2. Click "Start MFA Setup" to get your QR code and secret key</li>
                          <li>3. Scan the QR code or enter the secret key manually</li>
                          <li>4. Enter the 6-digit code from your app below</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                )}

                {/* MFA Code Input */}
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>
                    MFA Code *
                  </label>
                  <div className='relative'>
                    <input
                      type='text'
                      name='mfaCode'
                      value={form.mfaCode}
                      onChange={handleChange}
                      className='w-full px-3 py-2 pl-10 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
                      placeholder='Enter 6-digit code from your authenticator app'
                      maxLength={6}
                    />
                    <Smartphone className='absolute left-3 top-2.5 h-5 w-5 text-slate-400' />
                  </div>
                  <p className='text-xs text-slate-500 mt-1'>
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                {/* Action Buttons */}
                <div className='space-y-2'>
                  {!mfaSecret ? (
                    <Button
                      onClick={() => {
                        console.log('🔍 Start MFA Setup button clicked');
                        console.log('🔍 MFA Enabled:', mfaEnabled);
                        console.log('🔍 Loading:', loading);
                        console.log('🔍 Current MFA Secret:', mfaSecret);
                        handleMfaSetup();
                      }}
                      disabled={loading}
                      className='w-full'
                    >
                      {loading ? 'Starting MFA Setup...' : 'Start MFA Setup'}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleMfaVerification}
                      disabled={loading || !form.mfaCode || form.mfaCode.length !== 6}
                      className='w-full'
                    >
                      {loading ? 'Verifying...' : 'Complete MFA Setup'}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {!mfaEnabled && (
              <Button onClick={() => setStep('complete')} className='w-full'>
                Skip MFA Setup
              </Button>
            )}

            <div className='text-center'>
              <button
                type='button'
                onClick={() => setStep('password-reset')}
                className='text-sm text-slate-400 hover:text-slate-300 transition-colors'
              >
                ← Back to Password Setup
              </button>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className='text-center space-y-4'>
            <div className='flex justify-center'>
              <CheckCircle className='h-16 w-16 text-green-400' />
            </div>
            <h2 className='text-xl font-semibold text-slate-200'>Admin Setup Complete!</h2>
            <p className='text-slate-400'>
              Your admin account has been successfully configured. You can now access the admin
              portal.
            </p>
            <div className='bg-green-500/20 border border-green-500/30 rounded-lg p-4'>
              <h3 className='text-sm font-medium text-green-200 mb-2'>What's Next?</h3>
              <ul className='text-xs text-green-300 space-y-1 text-left'>
                <li>• Your admin password has been updated</li>
                {mfaEnabled && <li>• Multi-factor authentication is enabled</li>}
                <li>• You can now access the admin portal</li>
                <li>• Manage users, companies, and SFTP connectors</li>
              </ul>
            </div>
            <Button
              onClick={() => {
                markAuthed();
                navigate('/');
              }}
              className='w-full'
            >
              Access Admin Portal
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className='min-h-screen bg-slate-900 flex items-center justify-center p-4'>
      <div className='w-full max-w-md'>
        <Card title='🔐 Admin Account Setup' subtitle='Complete your admin account configuration'>
          {message && (
            <div
              className={`p-4 rounded-lg mb-6 ${
                message.startsWith('✅')
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-rose-500/20 text-rose-400'
              }`}
            >
              {message}
            </div>
          )}

          {renderStepContent()}
        </Card>
      </div>

      {/* Copyright with Easter egg */}
      <Copyright className='mt-8' showEasterEgg={true} />
    </div>
  );
}

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
